import { ChangeDetectorRef, Component, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { SentinelDoor, SentinelEntry, SentinelFacility } from "ng/src/app/log-settings/provisioning/sentinel-provision/sentinel-provision.model";
import { SentinelProvisioningService } from "ng/src/app/log-settings/provisioning/sentinel-provision/sentinel-provisioning.service";
import { PrimeIcons } from "primeng/api";
import { OverlayPanel } from "primeng/overlaypanel";
import { Observable, Subject, timer } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";
import { RxQR } from "ng/src/app/core/rxQR";

@Component({
  selector: 'sentinel-provision',
  templateUrl: './sentinel-provision.component.html',
  styleUrls: ['./sentinel-provision.component.scss'],
  providers: [SentinelProvisioningService]
})
export class SentinelProvision implements OnDestroy {
  @ViewChild('deleteDoorConfirm') deleteDoorConfirm!: OverlayPanel;
  @ViewChild('deleteFacilityConfirm') deleteFacilityConfirm!: OverlayPanel;
  @ViewChild('provisionDoorConfirm') provisionDoorConfirm!: OverlayPanel;
  
  readonly entries$: Observable<SentinelEntry[]>;
  
  readonly provisionCodes: Record<number, Promise<string>> = {};
  
  pendingProvision?: SentinelDoor;
  pendingDeleteDoor?: SentinelDoor;
  pendingDeleteFacility?: SentinelEntry;
  
  qrCodeDataUrl?: { code: string, dataUrl: string };
  
  get selectedFacility() {
    return this.sentinelProvisioningService.selectedFacility;
  }
  
  set selectedFacility(facility: SentinelEntry | undefined) {
    this.sentinelProvisioningService.selectedFacility = facility;
  }
  
  readonly icons = PrimeIcons;
  
  private _onDestroy = new Subject<void>();
  
  constructor(
    public sentinelProvisioningService: SentinelProvisioningService,
    private changeRef: ChangeDetectorRef
  ) {
    this.entries$ = this.sentinelProvisioningService.entries;
  }
  
  supportsClipboard() {
    return window?.navigator?.clipboard !== undefined;
  }
  
  copyCodeToClipboard(code: string) {
    if (!this.supportsClipboard()) {
      return;
    }
    
    return window.navigator.clipboard.writeText(code);
  }
  
  reProvision(doorId: number) {
    const p = this.sentinelProvisioningService.provisionDoor(doorId)
      .then((code) => {
        this.changeRef.detectChanges();
        
        timer(1000 * 60 * 4.5).pipe(
          takeUntil(this._onDestroy),
          take(1)
        ).subscribe(() => {
          if (this.provisionCodes[doorId] === p) {
            delete this.provisionCodes[doorId];
          }
          if (this.qrCodeDataUrl?.code === code) {
            this.qrCodeDataUrl = undefined;
          }
          this.changeRef.detectChanges();
        });
        
        return code;
      });
    
    this.provisionCodes[doorId] = p;
  }
  
  createCodeDataUrl(code: string) {
    RxQR.toDataURL(code).subscribe((dataUrl) => {
      this.qrCodeDataUrl = { code, dataUrl };
      this.changeRef.detectChanges();
    });
  }
  
  confirmProvision(ev: Event, door: SentinelDoor) {
    ev.stopPropagation();
    
    this.pendingProvision = door;
    this.provisionDoorConfirm.toggle(ev);
  }
  
  confirmFacilityDelete(ev: Event, facility: SentinelEntry) {
    ev.stopPropagation();
    
    this.pendingDeleteFacility = facility;
    this.deleteFacilityConfirm.toggle(ev);
  }
  
  confirmDoorDelete(ev: Event, door: SentinelDoor) {
    ev.stopPropagation();
    
    this.pendingDeleteDoor = door;
    this.deleteDoorConfirm.toggle(ev);
  }
  
  async deleteEntry(entry: SentinelEntry) {
    await this.sentinelProvisioningService.deleteFacility(entry);
    this.changeRef.detectChanges();
  }
  
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(ev: Event) {
    if (this.qrCodeDataUrl) {
      this.qrCodeDataUrl = undefined;
      ev.stopPropagation();
    }
  }
  
  ngOnDestroy(): void {
    this._onDestroy.next();
  }
}